import React from 'react';
import PropTypes from 'prop-types';

import { ChannelIcon } from '~/shared-components';
import { Bell, Phone, Warning } from '@bufferapp/ui/Icon';
import { redDark } from '@bufferapp/ui/style/colors';

import { usePostPermissions } from '~/post/hooks';
import MediaThumbnail from '../../../MediaThumbnail';
import { isDraft as checkIsDraft } from '../../../../utils/isDraft';
import {
  ApprovalTag,
  DraftTag,
  EndContent,
  Error,
  ErrorCard,
  Icon,
  MiddleContent,
  PostContentWrapper,
  PostText,
  StartContent,
  TagContainer,
  Time,
  BlueprintTag,
} from './styles';

const Reminder = ({ small, text }) => {
  return (
    <Icon small={small}>
      <Bell color="white" role="img" title={text} />
    </Icon>
  );
};

Reminder.propTypes = {
  small: PropTypes.bool.isRequired,
  text: PropTypes.string.isRequired,
};

const TiktokIcon = ({ small, text }) => {
  return (
    <Icon small={small} color="grey">
      <Phone color="white" role="img" title={text} />
    </Icon>
  );
};

TiktokIcon.propTypes = {
  small: PropTypes.bool.isRequired,
  text: PropTypes.string.isRequired,
};

const PostContent = ({ small, post }) => {
  const {
    status,
    text,
    type,
    media,
    needsApproval,
    channel,
    isReminder,
    formattedTime,
    channelData,
    isBlueprint,
  } = post;
  const { isManager } = usePostPermissions(post);
  const hasMedia = post?.media?.length > 0;
  const hasError = status === 'error';
  const isDraft = checkIsDraft(type);
  const showMediaThumbnail = hasMedia && !(small && hasError);

  const renderDraftTag = isDraft && !needsApproval && !isBlueprint;
  const renderApprovalTag = isDraft && needsApproval;
  const renderBlueprintTag = isBlueprint && isDraft;
  const renderTime = small && !isDraft;
  const renderSmallError = hasError && small;
  const renderError = hasError && !small;

  return (
    <PostContentWrapper small={small}>
      {/* Social Channel and Reminder Icon */}
      <StartContent small={small}>
        <ChannelIcon
          network={channel.service}
          circular
          role="img"
          title={`${channel.username} ${channel.service} post`}
        />
        {/* Show reminder icon here if large item */}
        {!small && isReminder && (
          <Reminder
            small={small}
            text="You'll receive a reminder in your Buffer app"
          />
        )}
      </StartContent>

      {/* Tags, Time, Post Text */}
      <MiddleContent small={small}>
        {renderDraftTag && (
          <TagContainer>
            <DraftTag>Draft</DraftTag>
          </TagContainer>
        )}
        {renderBlueprintTag && (
          <TagContainer>
            <BlueprintTag>Recipe</BlueprintTag>
          </TagContainer>
        )}
        {renderApprovalTag && (
          <TagContainer>
            <ApprovalTag>
              {isManager ? 'Awaiting Approval' : 'Pending Approval'}
            </ApprovalTag>
          </TagContainer>
        )}
        {renderTime && <Time type="p">{formattedTime}</Time>}

        {/* GBP Post Title */}
        {!small && channelData?.googlebusiness?.title && (
          <PostText type="p" isDraft={isDraft}>
            {channelData.googlebusiness.title}
          </PostText>
        )}

        {/* Post Text */}
        {/* Do not show text when there is a GBP Post Title */}
        {!small && !channelData?.googlebusiness?.title && (
          <PostText type="p" isDraft={isDraft}>
            {text}
          </PostText>
        )}
        {/* Show reminder icon here if small item */}
        {small && isReminder && (
          <Reminder
            small={small}
            text="You'll receive a reminder in your Buffer app"
          />
        )}
      </MiddleContent>

      {/* Media Thumbnail */}
      {showMediaThumbnail && (
        <EndContent>
          <MediaThumbnail media={media} size={small ? 'extraSmall' : 'small'} />
        </EndContent>
      )}

      {/* Error flags */}
      {renderError && (
        <Error>
          <Warning color={redDark} />
        </Error>
      )}
      {renderSmallError && (
        <EndContent>
          <ErrorCard>
            <Warning color="white" />
          </ErrorCard>
        </EndContent>
      )}
    </PostContentWrapper>
  );
};

PostContent.propTypes = {
  small: PropTypes.bool,
  post: PropTypes.shape({
    campaign: PropTypes.shape({
      color: PropTypes.shape({
        value: PropTypes.string,
      }),
    }),
    channel: PropTypes.shape({
      id: PropTypes.string,
      service: PropTypes.string,
      username: PropTypes.string,
    }),
    media: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        thumbnail: PropTypes.string,
      })
    ),
    text: PropTypes.string,
    status: PropTypes.string,
    isReminder: PropTypes.bool,
    formattedTime: PropTypes.string,
    type: PropTypes.string.isRequired,
    needsApproval: PropTypes.bool,
    updateType: PropTypes.string,
    channelData: PropTypes.shape({
      googlebusiness: PropTypes.shape({
        end_date: PropTypes.number,
        end_time: PropTypes.string,
        link: PropTypes.string,
        start_date: PropTypes.number,
        start_time: PropTypes.string,
        title: PropTypes.string,
      }),
    }),
  }),
};

PostContent.defaultProps = {
  small: false,
  post: {},
};

export default PostContent;
